// Helpers for the normalized coin media object the API returns
// (`coin.coinMedia` on user coins, `reward.media` on collection rewards).
//
//   { type: 'STATIC' | 'ANIMATED', isSignature, staticUrl, posterUrl,
//     animationUrl, shareVideoUrl, shareGifUrl }
//
// Older API responses / cached objects have no media object at all — those are
// treated as static coins using their existing image URL.

export function resolveCoinMedia(source, fallbackUrl = null) {
    const media = source?.coinMedia || source?.media || null;
    const legacyUrl = fallbackUrl ?? source?.coinImageUrl ?? null;

    if (media && media.type === 'ANIMATED' && media.animationUrl) {
        return {
            animationInset: 1, // < 1: play the animation centred inside posterUrl (the ringed coin)
            artPosterUrl: null,
            ...media,
            posterUrl: media.posterUrl || legacyUrl,
            staticUrl: media.staticUrl || media.posterUrl || legacyUrl,
        };
    }
    const staticUrl = (media && media.type === 'STATIC' && (media.staticUrl || media.posterUrl)) || legacyUrl;
    return {
        type: 'STATIC',
        isSignature: false,
        staticUrl,
        posterUrl: staticUrl,
        animationUrl: null,
        shareVideoUrl: null,
        shareGifUrl: null,
    };
}

export function isSignatureCoin(source) {
    return resolveCoinMedia(source).type === 'ANIMATED';
}

export const SHARE_DESTINATIONS = {
    MESSAGING: 'messaging',   // SMS/MMS, iMessage, WhatsApp… — inline GIF loops best
    SOCIAL_VIDEO: 'social_video', // feeds that prefer video
    LINK_PREVIEW: 'link_preview', // Open Graph / unfurls — static only
    GENERIC: 'generic',
};

const PRIORITY = {
    [SHARE_DESTINATIONS.MESSAGING]: ['gif', 'video', 'image'],
    [SHARE_DESTINATIONS.SOCIAL_VIDEO]: ['video', 'gif', 'image'],
    [SHARE_DESTINATIONS.LINK_PREVIEW]: ['image'],
    [SHARE_DESTINATIONS.GENERIC]: ['video', 'gif', 'image'],
};

const MIME = { gif: 'image/gif', video: 'video/mp4', image: 'image/png' };

/**
 * Picks the best asset to attach when sharing a coin.
 *
 * @param {object} opts
 * @param {object} opts.coin          coin or reward object from the API
 * @param {string} [opts.destination] one of SHARE_DESTINATIONS
 * @param {object} [opts.capabilities] what the share target can take:
 *        { gif, video, image } — omit a key (or pass true) when supported
 * @returns {{ kind: 'gif'|'video'|'image', url: string, mimeType: string } | null}
 */
export function getShareAsset({ coin, destination = SHARE_DESTINATIONS.GENERIC, capabilities = {} } = {}) {
    const media = resolveCoinMedia(coin);
    const urls = {
        gif: media.shareGifUrl,
        video: media.shareVideoUrl,
        image: media.staticUrl || media.posterUrl,
    };
    const order = PRIORITY[destination] || PRIORITY[SHARE_DESTINATIONS.GENERIC];

    for (const kind of order) {
        if (capabilities[kind] === false) continue;
        if (urls[kind]) return { kind, url: urls[kind], mimeType: MIME[kind] };
    }
    return null;
}

function guessDestination() {
    // The Web Share sheet on phones is dominated by messaging targets, where an
    // inline GIF loops best; desktop share targets handle video better.
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    return /iphone|ipad|android/i.test(ua) ? SHARE_DESTINATIONS.MESSAGING : SHARE_DESTINATIONS.SOCIAL_VIDEO;
}

/**
 * Shares a coin through the Web Share API, attaching the best media file the
 * browser accepts (GIF / MP4 / static image) and always including the public
 * URL — whose link preview uses the static poster. Returns:
 *   'shared'       shared (with or without a file)
 *   'unsupported'  no Web Share API — caller should fall back to copy-link
 *   'cancelled'    user dismissed the sheet
 */
export async function shareCoin({ coin, title, text, url, destination } = {}) {
    if (typeof navigator === 'undefined' || !navigator.share) return 'unsupported';

    const payload = { title, text, url };
    try {
        const asset = getShareAsset({ coin, destination: destination || guessDestination() });
        if (asset && navigator.canShare) {
            const blob = await (await fetch(asset.url)).blob();
            const ext = asset.kind === 'gif' ? 'gif' : asset.kind === 'video' ? 'mp4' : 'png';
            const file = new File([blob], `aluvo-coin.${ext}`, { type: asset.mimeType });
            if (navigator.canShare({ files: [file] })) payload.files = [file];
        }
    } catch {
        // Could not fetch/attach the media (CORS, offline…) — share the link only.
    }

    try {
        await navigator.share(payload);
        return 'shared';
    } catch (err) {
        return err?.name === 'AbortError' ? 'cancelled' : 'unsupported';
    }
}
